/**
 * A right-click where the surface owns the menu (`FrameRenderer.contextMenu`):
 * an opened item's ⋯ (opened-window rule 7), led by Copy selection when the
 * click landed on selected text, or a Pinned tile's card menu. A text field
 * keeps the system text menu, and a click with no menu item passes through.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { isTextEntryTarget } from "@/lib/editable-target";
import type { FrameData } from "@/lib/frame-data";
import { selectFrameWindow } from "@/lib/focus-frame-activation";
import { useFrame } from "@/stores/frames-selectors";
import {
  NO_MENU_EXTRAS,
  useFrameChromeConfigValue,
} from "./frame-chrome-config";
import { useFrameRenderer } from "./frame-renderer-context";
import {
  bridgeIframeContextMenus,
  contextMenuSelection,
} from "./iframe-context-menu-bridge";

interface OpenedFrameContextMenuProps {
  frameId: string;
  fallbackFrame?: FrameData;
  children: ReactNode;
}

export function OpenedFrameContextMenu({
  frameId,
  fallbackFrame,
  children,
}: OpenedFrameContextMenuProps) {
  const renderer = useFrameRenderer();
  const config = useFrameChromeConfigValue();
  const frame = useFrame(frameId) ?? fallbackFrame ?? null;
  const rootRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<ContextMenuAnchor | null>(null);
  // Kept while the menu fades out.
  const [selection, setSelection] = useState("");

  useEffect(() => {
    const root = rootRef.current;
    if (root) return bridgeIframeContextMenus(root);
  }, []);

  const extras = config.menuItems ?? NO_MENU_EXTRAS;
  const owner = renderer.contextMenu;
  // Stable, so the menu reads the frame's content only when it changes.
  const frameRoot = useCallback(
    () =>
      rootRef.current?.querySelector<HTMLElement>("[data-canvas-frame-id]") ??
      null,
    [],
  );
  /** Opens the menu when it has an item; false lets the event through. */
  const openAt = (next: ContextMenuAnchor, selected: string): boolean => {
    if (!frame || !owner?.opens(frame, extras, selected)) return false;
    // A Pinned tile (`chrome: "none"`) is never a window to bring forward.
    if (renderer.chrome !== "none") selectFrameWindow(frameId);
    setSelection(selected);
    setAnchor(next);
    return true;
  };

  return (
    <>
      <div
        ref={rootRef}
        className="contents"
        onContextMenu={(event) => {
          // A portaled child (its own menu, a dialog) is not the frame.
          if (!event.currentTarget.contains(event.target as Node)) return;
          if (isTextEntryTarget(event.target)) return;
          const opened = openAt(
            { x: event.clientX, y: event.clientY },
            contextMenuSelection(rootRef.current, event.target),
          );
          if (opened) event.preventDefault();
        }}
        onKeyDown={(event) => {
          if (
            event.key !== "ContextMenu" &&
            !(event.shiftKey && event.key === "F10")
          )
            return;
          if (!event.currentTarget.contains(event.target as Node)) return;
          if (isTextEntryTarget(event.target)) return;
          const target =
            event.target instanceof HTMLElement
              ? event.target
              : event.currentTarget;
          const rect = target.getBoundingClientRect();
          if (openAt({ x: rect.left + 8, y: rect.bottom }, "")) {
            event.preventDefault();
          }
        }}
        data-testid={`frame-context-menu-target-${frameId}`}
      >
        {children}
      </div>
      {frame &&
        owner?.render({
          frame,
          extras,
          anchor,
          selection,
          frameRoot,
          onClose: () => setAnchor(null),
        })}
    </>
  );
}
